import { useMemo } from 'react';

import { InfoIcon, RotateCwIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Button } from '@/components/ui/button';
import useBrains from '@/hooks/useBrains';
import { ModelPicker } from '@/modules/aiAgents/components/ModelStrategy/ModelPicker';
import { ReasoningEffortSelect } from '@/modules/aiAgents/components/ModelStrategy/ReasoningEffortSelect';
import { useModelOverride } from '@/modules/tryIt/hooks/useModelOverride';
import { ModelStrategyStep, ReasoningEffort } from '@/shared/types/modelStrategy';

import { FallbackChain } from './FallbackChain';
import { selectBrainId } from '../../../redux/selectors';

/**
 * Test-mode-only Model override tab. Fields that don't apply are hidden rather
 * than disabled.
 */
export const ModelPanel = () => {
  const { t } = useTranslation();
  const brainId = useSelector(selectBrainId);
  const { brain } = useBrains(brainId);

  // The agent's own model, so the picker reflects the default rather than an
  // empty selection.
  const defaultModel = useMemo<ModelStrategyStep | undefined>(
    () =>
      brain?.language_model_id
        ? {
            language_model_id: brain.language_model_id,
            language_model_alias: null,
            reasoning_effort: null,
          }
        : undefined,
    [brain?.language_model_id]
  );

  const {
    buckets,
    pickerValue,
    isOverrideActive,
    supportsReasoning,
    updateOverride,
    clearOverride,
  } = useModelOverride(brainId, defaultModel);

  return (
    <div className="flex w-full flex-col items-start gap-6">
      <div className="flex w-full items-start gap-4">
        <div className="flex max-w-96 min-w-0 flex-1 flex-col items-start gap-2">
          <span className="text-sm leading-5 font-medium text-foreground">
            {t('try_it.model_override.panel_label')}
          </span>
          <ModelPicker
            value={pickerValue}
            buckets={buckets}
            contentClassName="z-3000"
            placeholder={t('common.select_dots')}
            onChange={(next) =>
              updateOverride({
                language_model_id: next.language_model_id,
                language_model_alias: next.language_model_alias,
              })
            }
          />
          <p className="text-sm leading-5 font-normal text-muted-foreground">
            {t('try_it.model_override.panel_description')}
          </p>
        </div>

        <div className="flex w-45 shrink-0 flex-col items-start gap-2">
          {supportsReasoning && (
            <>
              <span className="flex items-center gap-1 text-sm leading-5 font-medium text-foreground">
                {t('ai_agents.model_strategy.reasoning_label')}
                <InfoTooltip content={t('try_it.model_override.reasoning_tooltip')} size="large">
                  <InfoIcon className="size-3 shrink-0 text-muted-foreground" />
                </InfoTooltip>
              </span>
              <ReasoningEffortSelect
                value={pickerValue.reasoning_effort}
                contentClassName="z-3000"
                triggerClassName="bg-background"
                onChange={(next: ReasoningEffort) => updateOverride({ reasoning_effort: next })}
              />
            </>
          )}
        </div>
      </div>

      {isOverrideActive ? (
        <Button type="button" variant="ghost" size="sm" onClick={clearOverride}>
          <RotateCwIcon />
          {t('try_it.model_override.clear')}
        </Button>
      ) : (
        <FallbackChain brainId={brainId} legacyLanguageModelId={brain?.language_model_id} />
      )}
    </div>
  );
};
